import React, { Component } from 'react';
import Modal from '../../components/Modal';
import { getAuthHeaders, urlMap } from '../../../utils';

/**
 * Reusable Instagram connection modal for the Social Media MVP.
 *
 * Runs the real Instagram Graph API (Meta) OAuth flow:
 *   1. Ask the social service for the Facebook login URL.
 *   2. Open it in a popup; the backend callback posts the result back.
 *   3. On success, pull the latest posts as proof the account is truly linked.
 *
 * Props:
 *  - isOpen      : boolean
 *  - onClose     : () => void
 *  - handle      : string        detected instagram handle (without @)
 *  - projectId   : string | null
 *  - connected   : boolean       account already connected (show posts on open)
 *  - onConnected : (connection) => void
 */
class InstagramConnectModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'idle', // idle | connecting | connected | error
      error: '',
      connection: null,
      media: [],
      loadingMedia: false,
    };
    this.popup = null;
    this.popupTimer = null;
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessage);
    if (this.props.isOpen && this.props.connected) this.showConnected();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessage);
    this.clearPopupWatch();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      if (this.props.connected) {
        this.showConnected();
      } else {
        this.setState({ status: 'idle', error: '', connection: null, media: [], loadingMedia: false });
      }
    }
  }

  withProject = (url) => {
    const pid = this.props.projectId;
    return pid ? `${url}?projectId=${encodeURIComponent(pid)}` : url;
  };

  showConnected = () => {
    this.setState({ status: 'connected', error: '' });
    this.loadMedia();
  };

  clearPopupWatch = () => {
    if (this.popupTimer) {
      clearInterval(this.popupTimer);
      this.popupTimer = null;
    }
  };

  friendlyError = (code) => {
    switch (code) {
      case 'NO_INSTAGRAM_BUSINESS_ACCOUNT':
        return 'No Instagram Business account was found on that Facebook login. Make sure your Instagram is a Business/Creator account linked to your Facebook Page.';
      case 'INSTAGRAM_NOT_CONFIGURED':
        return 'Instagram connection is not configured on the server yet.';
      case 'STATE_EXPIRED':
        return 'The connection window timed out. Please try again.';
      case 'CONNECTION_EXPIRED':
        return 'The Instagram connection expired. Please reconnect.';
      case 'access_denied':
        return 'You cancelled the Facebook authorization.';
      default:
        return code ? `Could not connect Instagram (${code}).` : 'Could not connect Instagram.';
    }
  };

  handleMessage = (event) => {
    const data = event?.data;
    if (!data || data.source !== 'storyclub-instagram') return;
    // Only trust a message from the popup we opened.
    if (this.popup && event.source && event.source !== this.popup) return;

    this.clearPopupWatch();
    if (data.ok) {
      this.setState({
        status: 'connected',
        error: '',
        connection: { igUsername: data.username || null, igBusinessId: data.igBusinessId || null },
      });
      this.loadMedia();
    } else {
      this.setState({ status: 'error', error: this.friendlyError(data.error) });
    }
  };

  handleConnect = async () => {
    this.setState({ status: 'connecting', error: '' });
    try {
      const res = await fetch(this.withProject(urlMap.socialInstagramStart), {
        headers: { ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.error || `HTTP_${res.status}`);

      const w = 600;
      const h = 720;
      const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
      this.popup = window.open(
        data.url,
        'storyclub_ig_oauth',
        `width=${w},height=${h},left=${left},top=${top}`
      );

      if (!this.popup) {
        this.setState({ status: 'error', error: 'Please allow pop-ups to connect Instagram.' });
        return;
      }

      // Detect the popup being closed without completing.
      this.clearPopupWatch();
      this.popupTimer = setInterval(() => {
        if (this.popup && this.popup.closed) {
          this.clearPopupWatch();
          this.setState((s) =>
            s.status === 'connecting'
              ? { status: 'error', error: 'The connection window was closed before finishing.' }
              : null
          );
        }
      }, 700);
    } catch (err) {
      this.setState({ status: 'error', error: this.friendlyError(err?.message) });
    }
  };

  loadMedia = async () => {
    this.setState({ loadingMedia: true });
    try {
      const res = await fetch(this.withProject(urlMap.socialInstagramMedia), {
        headers: { ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
      this.setState((s) => ({
        media: Array.isArray(data.media) ? data.media : [],
        loadingMedia: false,
        connection: {
          igUsername: data.igUsername || s.connection?.igUsername || null,
          igBusinessId: data.igBusinessId || s.connection?.igBusinessId || null,
        },
      }));
    } catch (err) {
      // Connection is saved even if media fails; surface a soft note.
      this.setState({ loadingMedia: false, error: this.friendlyError(err?.message) });
    }
  };

  handleDone = () => {
    this.props.onConnected?.(this.state.connection);
    this.props.onClose?.();
  };

  renderMedia() {
    const { media, loadingMedia } = this.state;
    if (loadingMedia) {
      return <p className="ig-connect-modal__text">Loading your latest posts…</p>;
    }
    if (!media.length) {
      return <p className="ig-connect-modal__text">No recent posts found on this account yet.</p>;
    }
    return (
      <div className="ig-media-grid">
        {media.map((m) => {
          const img = m.thumbnailUrl || m.mediaUrl;
          return (
            <a
              key={m.id}
              className="ig-media-grid__item"
              href={m.permalink || '#'}
              target="_blank"
              rel="noreferrer"
              title={m.caption || ''}
            >
              {img ? (
                <img src={img} alt={m.caption ? m.caption.slice(0, 60) : 'Instagram post'} loading="lazy" />
              ) : (
                <span className="ig-media-grid__placeholder">{m.mediaType || 'POST'}</span>
              )}
            </a>
          );
        })}
      </div>
    );
  }

  render() {
    const { isOpen, onClose, handle } = this.props;
    const { status, error, connection } = this.state;
    const connectedName = connection?.igUsername
      ? `@${connection.igUsername}`
      : handle
      ? `@${handle}`
      : 'your account';
    const displayHandle = handle ? `@${handle}` : 'your Instagram account';

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="ig-connect-modal">
          <div className="ig-connect-modal__badge" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
            </svg>
          </div>

          {status === 'connected' ? (
            <>
              <h2 className="ig-connect-modal__title">Connected</h2>
              <p className="ig-connect-modal__text">
                <strong>{connectedName}</strong> is linked. Here are your latest posts pulled live from
                Instagram — proof the connection is working.
              </p>

              {error && (
                <div className="ig-connect-modal__error" role="alert">
                  {error}
                </div>
              )}

              {this.renderMedia()}

              <button type="button" className="btn primary ig-connect-modal__cta" onClick={this.handleDone}>
                Done
              </button>
            </>
          ) : (
            <>
              <h2 className="ig-connect-modal__title">Connect Instagram</h2>
              <p className="ig-connect-modal__text">
                We&apos;ll open Facebook to authorize <strong>{displayHandle}</strong>. Your Instagram
                must be a Business/Creator account linked to a Facebook Page.
              </p>

              {status === 'error' && (
                <div className="ig-connect-modal__error" role="alert">
                  {error}
                </div>
              )}

              <div className="ig-connect-modal__actions">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={onClose}
                  disabled={status === 'connecting'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={this.handleConnect}
                  disabled={status === 'connecting'}
                >
                  {status === 'connecting'
                    ? 'Waiting for Facebook…'
                    : status === 'error'
                    ? 'Try again'
                    : 'Connect with Facebook'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }
}

export default InstagramConnectModal;
